import { useEffect, useState, useCallback } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminAssociations() {
  const [items, setItems] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [filterDoctor, setFilterDoctor] = useState("");
  const [filterClinic, setFilterClinic] = useState("");
  const [newDoctor, setNewDoctor] = useState("");
  const [newClinic, setNewClinic] = useState("");

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (filterDoctor) p.set("doctor_id", filterDoctor);
    if (filterClinic) p.set("clinic_id", filterClinic);
    const { data } = await api.get(`/admin/associations${p.toString() ? "?" + p.toString() : ""}`);
    setItems(data.items);
  }, [filterDoctor, filterClinic]);

  useEffect(() => {
    api.get("/admin/doctors?page_size=200").then((r) => setDoctors(r.data.items));
    api.get("/admin/clinics?page_size=200").then((r) => setClinics(r.data.items));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newDoctor || !newClinic) return;
    try {
      await api.post("/admin/associations", { doctor_id: newDoctor, clinic_id: newClinic });
      toast.success("Association added");
      setNewDoctor(""); setNewClinic("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/associations/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <AdminLayout title="Doctor ↔ Clinic Associations" subtitle={`${items.length} link${items.length === 1 ? "" : "s"}`}>
      <section className="bg-white border border-border rounded-2xl p-4 mb-4">
        <div className="text-sm font-semibold mb-3">Add new association</div>
        <div className="flex flex-wrap gap-2">
          <Select value={newDoctor} onValueChange={setNewDoctor}>
            <SelectTrigger data-testid="assoc-new-doctor" className="w-60"><SelectValue placeholder="Select doctor" /></SelectTrigger>
            <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={newClinic} onValueChange={setNewClinic}>
            <SelectTrigger data-testid="assoc-new-clinic" className="w-60"><SelectValue placeholder="Select clinic" /></SelectTrigger>
            <SelectContent>{clinics.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="assoc-add-link-btn" onClick={add} disabled={!newDoctor || !newClinic}><Plus className="h-4 w-4 mr-1" />Add link</Button>
        </div>
      </section>

      <section className="flex flex-wrap gap-2 mb-3">
        <Select value={filterDoctor || "all"} onValueChange={(v) => setFilterDoctor(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="assoc-filter-doctor" className="w-56 rounded-full"><SelectValue placeholder="Filter by doctor" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All doctors</SelectItem>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterClinic || "all"} onValueChange={(v) => setFilterClinic(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="assoc-filter-clinic" className="w-56 rounded-full"><SelectValue placeholder="Filter by clinic" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All clinics</SelectItem>{clinics.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </section>

      <div data-testid="admin-associations-table" className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left p-3">Doctor</th><th className="text-left p-3">Clinic</th><th className="text-left p-3">Created</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No associations</td></tr>
                : items.map((a) => (
                <tr key={a.id} data-testid={`assoc-row-${a.id}`} className="border-t border-border">
                  <td className="p-3"><div className="font-medium">{a.doctor?.name}</div><div className="text-xs text-muted-foreground">{a.doctor?.category}</div></td>
                  <td className="p-3"><div className="font-medium">{a.clinic?.name}</div><div className="text-xs text-muted-foreground">{a.clinic?.city}</div></td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right"><Button data-testid={`assoc-remove-btn-${a.id}`} size="sm" variant="ghost" onClick={() => remove(a.id)}><X className="h-4 w-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
